import { useEffect, useState, useRef } from "react";
import NavigationButton from "../../components/ui/NavigationButton";
import GeneralForm from "../../components/forms/generalForm/GeneralForm";
import { useLocation, useNavigate } from "react-router-dom";
import BackButton from "../../components/ui/BackNavigationButton";
import Breadcrumbs from "../../components/ui/Breadcrumbs";
import { pages } from "../../components/utils/pages-links";
import TutorialOverlay from "../../components/ui/TutorialOverlay";
import { useAlert } from "../../context/alert/AlertProvider";
import { useTranslation } from 'react-i18next';
import ScenarioIndicator from "../../components/forms/scenario/ScenarioIndicator";
import useAutoSave from "../../hooks/useAutoSave";

function GeneralPage() {
  const navigate = useNavigate();
  const [mode, setMode] = useState('weeks');
  const [info, setInfo] = useState({
    weeks: '',
    days: '',
    cycle: '',
    offset: '',
    GA: '',
    OFFsum: '',
  });
  const [currentDate, setCurrentDate] = useState(new Date());
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [isSelectingEnd, setIsSelectingEnd] = useState(false);
  const generalformRef = useRef(null);
  const selectModeRef = useRef(null);
  const calendarRef = useRef(null);
  const [showTutorial, setShowTutorial] = useState(false);
  const [tutorialStep, setTutorialStep] = useState(0);
  const nextButtonRef = useRef(null);
  const location = useLocation();
  const {showAlert} = useAlert();
  const isExampleMode = location.state?.isExample;
  const { t } = useTranslation();
  
  const tutorialSteps = [
    {
      title: t("tutorial.general.title"),
      description: t("tutorial.general.description"),
      position:"center"
    },
    {
      title: t("tutorial.general.mode.title"),
      description: t("tutorial.general.mode.description"),
      position:"top-form",
      elementRef: selectModeRef
    },
    {
      title: t("tutorial.general.parameters.title"),
      description: t("tutorial.general.parameters.description"),
      position: "form-parameters",
      elementRef: generalformRef
    },
    {
      title: t("tutorial.general.calendar.title") || "Modo Calendário",
      description: t("tutorial.general.calendar.description") || "Quando o modo calendário está ativo, você pode selecionar datas específicas diretamente no calendário.",
      position: "calendar-section",
      elementRef: calendarRef,
      condition: () => mode === 'calendar'
    },
    {
      title: t("tutorial.general.next.title"),
      description: t("tutorial.general.next.description"),
      position: "next-button",
      elementRef: nextButtonRef
    }
  ].filter(step => !step.condition || step.condition());

  const processGeneralData = (data) => {
    const processedData = {
      ...data,
      week_or_days: mode === 'weeks' ? 0 : mode === 'days' ? 1 : 2, 
      weeks: mode === 'weeks' ? Number(data.weeks) : 0,
      days: mode === 'days' ? Number(data.days) : 0,
      cycle: 0,
      offset: 0,
      GA: 0,
      OFFsum: 0
    };

    if (mode === 'calendar' && startDate && endDate) {
      const calendarData = {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        duration: Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1
      };
      sessionStorage.setItem('calendar', JSON.stringify(calendarData));
    }

    return processedData;
  };

  useAutoSave('general', info, 500, processGeneralData);

  useEffect(() => {
    if(isExampleMode) {
      setShowTutorial(true);
    }

    const savedInfo = JSON.parse(sessionStorage.getItem('general'));
    if (savedInfo) {
      if ('week_or_days' in savedInfo) {
        let savedMode;
        switch(savedInfo.week_or_days) {
          case 0: savedMode = 'weeks'; break;
          case 1: savedMode = 'days'; break;
          case 2: savedMode = 'calendar'; break;
          default: savedMode = 'weeks';
        }
        setMode(savedMode);
        const { week_or_days, ...restInfo } = savedInfo;
        setInfo(restInfo);
      } else {
        setInfo(savedInfo);
      }
    }

    const savedCalendar = JSON.parse(sessionStorage.getItem('calendar'));
    if (savedCalendar) {
      if (savedCalendar.startDate) {
        setStartDate(new Date(savedCalendar.startDate));
      }
      if (savedCalendar.endDate) {
        setEndDate(new Date(savedCalendar.endDate));
      }
    }
  }, [isExampleMode]);

  const handleChange = (e) => {
    const value = e.target.value === '' ? '' : Number(e.target.value);
    setInfo(prev => ({
      ...prev,
      [e.target.name]: value
    }));
  };

  const handleModeChange = (newMode) => {
    setMode(newMode);
    setInfo(prev => ({
      ...prev,
      weeks: newMode === 'weeks' ? prev.weeks : '',
      days: newMode === 'days' ? prev.days : ''
    }));

    if (newMode !== 'calendar') {
      setStartDate(null);
      setEndDate(null);
      setIsSelectingEnd(false);
    }
  };

  const handleDateClick = (date) => {
    if (!startDate || isSelectingEnd) {
      if (!startDate) {
        setStartDate(date);
        setIsSelectingEnd(true);
      } else {
        if (date < startDate) {
          setEndDate(startDate);
          setStartDate(date);
        } else {
          setEndDate(date);
        }
        setIsSelectingEnd(false);
      }
    } else {
      setStartDate(date);
      setEndDate(null);
      setIsSelectingEnd(true);
    }
  };

  const clearCalendarSelection = () => {
    setStartDate(null);
    setEndDate(null);
    setIsSelectingEnd(false);
  };

  const handleNext = () => {
    if (mode === 'weeks' && (!info.weeks || info.weeks <= 0)) {
      showAlert({
        title: t("alert.validation.error") || 'Erro de Validação',
        description: t("general.validation.weeks") || 'Por favor, insira um número válido de semanas.',
        variant: 'destructive'
      });
      return;
    }

    if (mode === 'days' && (!info.days || info.days <= 0)) {
      showAlert({
        title: t("alert.validation.error") || 'Erro de Validação',
        description: t("general.validation.days") || 'Por favor, insira um número válido de dias.',
        variant: 'destructive'
      });
      return;
    }

    if (mode === 'calendar' && (!startDate || !endDate)) {
      showAlert({
        title: t("alert.validation.error") || 'Erro de Validação',
        description: t("calendar.validation.selectDates") || 'Por favor, selecione uma data de início e fim.',
        variant: 'destructive'
      });
      return;
    }

    navigate('/form/schedule', {
      state: { isExample: isExampleMode}
    });    
  };

  const handleNextTutorialStep = () => {
    if (tutorialStep < tutorialSteps.length - 1){
      setTutorialStep(prev => prev + 1);
    } else {
      setShowTutorial(false);
    }
  };

  const handleBack = () => {
    navigate('/form/collaborators' , {
      state: { isExample: isExampleMode }
    });
  };

  return (
    <div className="-mt-16">
      <Breadcrumbs pages={pages}/>
      <div className="p-6 bg-gray-100 min-h-screen pb-24">
        <div className="max-w-6xl mx-auto space-y-8">
            <ScenarioIndicator />

            <GeneralForm 
              info={info}
              handleChange={handleChange}
              mode={mode}
              onModeChange={handleModeChange}
              ref={generalformRef}
              selectModeRef={selectModeRef}
              isDisabled={isExampleMode}
              translations={t}
              currentDate={currentDate}
              setCurrentDate={setCurrentDate}
              startDate={startDate}
              endDate={endDate}
              isSelectingEnd={isSelectingEnd}
              handleDateClick={handleDateClick}
              clearSelection={clearCalendarSelection}
            />

          </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg z-40">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex justify-between items-center">
            <BackButton onClick={handleBack} />
            <NavigationButton ref={nextButtonRef} onClick={handleNext} />
          </div>
        </div>
      </div>

      <TutorialOverlay 
        isVisible={showTutorial}
        onClose={() => setShowTutorial(false)}
        title={tutorialSteps[tutorialStep]?.title}
        description={tutorialSteps[tutorialStep]?.description}
        position={tutorialSteps[tutorialStep]?.position}
        targetRef={tutorialSteps[tutorialStep]?.elementRef}
        onNext={handleNextTutorialStep}
        isLastStep={tutorialStep === tutorialSteps.length - 1}
      />
    </div>
  );
}

export default GeneralPage;